/**
 * Nagłówek kafelka pojedynczej oferty – analogicznie do TraderClientDataHeader
 */

import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useQueryClient } from 'react-query';
import { MenuItem, Select, Skeleton } from '@mui/material';

import type { ISalesCrmOffer } from 'api/endpoints/useGetSalesCrmClientOffer';
import type { ISalesCrmOfferVersionListItem } from 'api/endpoints/useGetSalesCrmOfferVersions';
import { usePutSalesCrmOffer } from 'api';
import { useSendOfferToClientFlow } from 'hooks';
import { WiseBadge } from 'components/wise';
import SendOfferToClientModal from 'components/containers/SendOfferToClientModal';

import styles from 'theme/components/containers/TraderClientCardContainer/TraderClientDataHeader/TraderClientDataHeader.module.scss';

interface ITraderClientOfferDataHeaderProps {
  offer?: ISalesCrmOffer | null;
  isLoading?: boolean;
  versions?: ISalesCrmOfferVersionListItem[];
  selectedVersionId?: number | null;
  onVersionChange?: (versionId: number | null) => void;
  /** Wymagane dla edycji statusu (PUT /offers/:id). */
  clientId?: number;
  offerId?: string;
}

const hasActiveStatus = (status: string | undefined): boolean =>
  (status ?? '').toLowerCase().includes('aktywn');

const OFFER_STATUS_OPTIONS: ReadonlyArray<{ id: number; labelKey: string }> = [
  { id: 1, labelKey: 'W negocjacji' },
  { id: 6, labelKey: 'Do akceptacji' },
  { id: 2, labelKey: 'Zaakceptowana' },
  { id: 7, labelKey: 'Zaakceptowana zewnętrznie' },
  { id: 3, labelKey: 'Odrzucona' },
  { id: 4, labelKey: 'Wygasła' },
  { id: 5, labelKey: 'Anulowana' }
];

const TraderClientOfferDataHeader: FC<ITraderClientOfferDataHeaderProps> = ({
  offer,
  isLoading = false,
  versions = [],
  selectedVersionId = null,
  onVersionChange,
  clientId,
  offerId
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showVersionSelect = versions.length > 1;
  const actualVersion = versions.find((v) => v.isActual) ?? null;
  const effectiveValue =
    selectedVersionId !== null ? selectedVersionId : actualVersion?.id ?? '';
  const canEditStatus = typeof clientId === 'number' && clientId > 0 && typeof offerId === 'string' && offerId.length > 0;
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const { mutate: putOffer, isLoading: isSavingStatus } = usePutSalesCrmOffer(
    clientId ?? 0,
    offerId ?? '',
    {
      onSuccess: () => {
        if (clientId && offerId) {
          // Inwalidujemy każdy wariant klucza oferty (różne versionId).
          queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId]);
        }
      }
    }
  );

  const sendFlow = useSendOfferToClientFlow({
    clientId: clientId ?? 0,
    offerId: offerId ?? '',
    acceptanceUrl: offer?.acceptance_url ?? null
  });

  const handleSendOnly = async () => {
    await sendFlow.sendOnly();
    setIsSendModalOpen(false);
  };

  const handleSendAndOpen = async () => {
    await sendFlow.sendAndOpen();
    setIsSendModalOpen(false);
  };

  if (isLoading) {
    return (
      <div
        className={classnames(
          styles.clientDataHeader,
          'StylePath-Containers-TraderClientOfferContainer-TraderClientOfferDataHeader'
        )}>
        <div className={styles.topRow}>
          <div className={styles.badgesRow}>
            <Skeleton variant="rounded" width={64} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
          </div>
        </div>
        <div className={styles.itemsRow}>
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={90} height={20} />
        </div>
      </div>
    );
  }

  if (!offer) {
    return null;
  }

  const showActiveBadge = hasActiveStatus(offer.status);

  return (
    <div
      className={classnames(
        styles.clientDataHeader,
        'StylePath-Containers-TraderClientOfferContainer-TraderClientOfferDataHeader'
      )}>
      <div className={styles.topRow}>
        <div className={styles.badgesRow}>
          {showActiveBadge && <WiseBadge variant="active" size="small" />}
        </div>
      </div>
      <div className={styles.itemsRow}>
        <div className={styles.clientDataHeaderItem}>
          <span className={styles.clientDataHeaderLabel}>{t('Numer oferty')}:</span>
          <span className={styles.clientDataHeaderValue}>{offer.offer_number || '–'}</span>
        </div>
        <div className={styles.clientDataHeaderItem}>
          <span className={styles.clientDataHeaderLabel}>{t('Id oferty')}:</span>
          <span className={styles.clientDataHeaderValue}>{offer.id}</span>
        </div>
        <div className={styles.clientDataHeaderItem}>
          <span className={styles.clientDataHeaderLabel}>{t('Status')}:</span>
          {canEditStatus ? (
            <>
              <Select
                size="small"
                value={offer.status_id ?? ''}
                disabled={isSavingStatus}
                onChange={(e) => {
                  const raw = e.target.value;
                  const nextId = typeof raw === 'number' ? raw : raw === '' ? null : Number(raw);
                  if (nextId !== null && nextId !== offer.status_id) {
                    putOffer({ status: nextId });
                  }
                }}
                sx={{ minWidth: 140, fontSize: 13, height: 28 }}>
                {OFFER_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {t(opt.labelKey)}
                  </MenuItem>
                ))}
              </Select>
              {offer.status_id === 1 && (
                <button
                  type="button"
                  onClick={() => setIsSendModalOpen(true)}
                  disabled={isSavingStatus || sendFlow.isSending}
                  style={{
                    marginLeft: 8,
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid #6b54ff',
                    background: '#6b54ff',
                    color: '#fff',
                    fontSize: 12,
                    cursor: 'pointer'
                  }}>
                  {t('Wyślij klientowi')}
                </button>
              )}
              {offer.status_id === 6 && (
                <button
                  type="button"
                  onClick={() => putOffer({ status: 1 })}
                  disabled={isSavingStatus}
                  style={{
                    marginLeft: 8,
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid #6b54ff',
                    background: '#fff',
                    color: '#6b54ff',
                    fontSize: 12,
                    cursor: 'pointer'
                  }}>
                  {t('Cofnij do edycji')}
                </button>
              )}
              {offer.status_id !== 1 && sendFlow.hasClientUser && (
                <button
                  type="button"
                  onClick={() => sendFlow.openInClientPanel()}
                  disabled={isSavingStatus || sendFlow.isSending}
                  style={{
                    marginLeft: 8,
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid #6b54ff',
                    background: '#fff',
                    color: '#6b54ff',
                    fontSize: 12,
                    cursor: 'pointer'
                  }}>
                  {sendFlow.isExternalAcceptance
                    ? t('Przejdz do oferty')
                    : t('Zobacz w panelu klienta')}
                </button>
              )}
            </>
          ) : (
            <span className={styles.clientDataHeaderValue}>{offer.status || '–'}</span>
          )}
        </div>
        {showVersionSelect && (
          <div className={styles.clientDataHeaderItem}>
            <span className={styles.clientDataHeaderLabel}>{t('Wersja')}:</span>
            <Select
              size="small"
              value={effectiveValue}
              onChange={(e) => {
                const raw = e.target.value;
                const next = raw === '' ? null : Number(raw);
                if (onVersionChange) onVersionChange(next ?? null);
              }}
              sx={{ minWidth: 120, fontSize: 13, height: 28 }}>
              {versions.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {t('Wersja')} {v.ordinal}
                  {v.isActual ? ` (${t('aktualna')})` : ''}
                </MenuItem>
              ))}
            </Select>
            {canEditStatus && typeof effectiveValue === 'number' && effectiveValue !== (actualVersion?.id ?? -1) && (
              <button
                type="button"
                onClick={() => {
                  putOffer(
                    { actualVersionId: Number(effectiveValue) },
                    {
                      onSuccess: () => {
                        queryClient.invalidateQueries([
                          'sales-crm',
                          'client',
                          clientId,
                          'offer',
                          offerId,
                          'versions'
                        ]);
                      }
                    }
                  );
                }}
                disabled={isSavingStatus}
                style={{
                  marginLeft: 8,
                  padding: '4px 10px',
                  borderRadius: 4,
                  border: '1px solid #6b54ff',
                  background: '#6b54ff',
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer'
                }}>
                {t('Ustaw jako aktualną')}
              </button>
            )}
          </div>
        )}
      </div>

      <SendOfferToClientModal
        open={isSendModalOpen}
        isSending={sendFlow.isSending}
        onClose={() => setIsSendModalOpen(false)}
        onSendOnly={handleSendOnly}
        onSendAndOpen={handleSendAndOpen}
        isExternalAcceptance={sendFlow.isExternalAcceptance}
      />
    </div>
  );
};

export default TraderClientOfferDataHeader;
